import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { usePublicData } from '../../context/PublicDataContext';
import { labelize, slugify, Eyebrow, Display, Skeleton } from './components/ui';
import ProductCard from './components/ProductCard';
import QuickViewModal from './components/QuickViewModal';
import Seo from './components/Seo';

const SORTS = {
  featured: { label: 'Featured', fn: (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id },
  newest: { label: 'Newest', fn: (a, b) => (new Date(b.created_at || 0)) - (new Date(a.created_at || 0)) || b.id - a.id },
  az: { label: 'A–Z', fn: (a, b) => (a.name || '').localeCompare(b.name || '') },
};

export default function ProductListingPage() {
  const { products, categories, loading, siteName } = usePublicData();
  const [params, setParams] = useSearchParams();
  const [quick, setQuick] = useState(null);
  const [sort, setSort] = useState('featured');

  const activeSlug = params.get('category') || '';
  const activeCat = categories.find((c) => slugify(c.name) === activeSlug);
  const title = activeCat ? labelize(activeCat.name) : 'The Collection';

  const list = useMemo(() => {
    let out = activeCat
      ? products.filter((p) => slugify(p.category || '') === activeSlug)
      : [...products];
    return out.sort(SORTS[sort].fn);
  }, [products, activeCat, activeSlug, sort]);

  const setCategory = (slug) => {
    const next = new URLSearchParams(params);
    if (slug) next.set('category', slug);
    else next.delete('category');
    setParams(next);
  };

  return (
    <>
      <Seo
        title={title}
        description={`Browse ${title.toLowerCase()} — handcrafted furniture by ${siteName}. Request a quote on any piece.`}
        siteName={siteName}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: '/' },
            { '@type': 'ListItem', position: 2, name: 'Products', item: '/products' },
            ...(activeCat ? [{ '@type': 'ListItem', position: 3, name: title }] : []),
          ],
        }}
      />

      {/* header band */}
      <section className="pt-36 pb-12 bg-[#ece0cd]">
        <div className="max-w-6xl mx-auto px-6">
          <nav className="flex items-center gap-2 text-[12px] text-[#4a3d32] mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#b5622f]">Home</Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <Link to="/products" className="hover:text-[#b5622f]">Products</Link>
            {activeCat && (<><ChevronRight className="w-3.5 h-3.5" /><span className="text-[#241811]">{title}</span></>)}
          </nav>
          <Eyebrow>Product range</Eyebrow>
          <Display as="h1" className="text-4xl md:text-[52px] mt-4">{title}</Display>
        </div>
      </section>

      {/* filter + sort bar */}
      <div className="sticky top-[var(--public-header-h,104px)] z-30 bg-[#f4ece0f2] backdrop-blur border-b border-[#241811]/10">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-3 overflow-x-auto">
          <button
            onClick={() => setCategory('')}
            className={`whitespace-nowrap text-[12px] uppercase tracking-[0.12em] px-4 py-2 border transition-colors ${!activeCat ? 'bg-[#241811] text-[#f4ece0] border-[#241811]' : 'border-[#241811]/20 hover:border-[#241811]'}`}
          >
            All
          </button>
          {categories.map((c) => {
            const on = slugify(c.name) === activeSlug;
            return (
              <button
                key={c.name}
                onClick={() => setCategory(slugify(c.name))}
                className={`whitespace-nowrap text-[12px] uppercase tracking-[0.12em] px-4 py-2 border transition-colors ${on ? 'bg-[#241811] text-[#f4ece0] border-[#241811]' : 'border-[#241811]/20 hover:border-[#241811]'}`}
              >
                {labelize(c.name)}
              </button>
            );
          })}
          <div className="ml-auto flex-shrink-0">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="text-[12px] uppercase tracking-[0.1em] bg-transparent border border-[#241811]/20 px-3 py-2 outline-none focus:border-[#b5622f]"
              aria-label="Sort products"
            >
              {Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* grid */}
      <section className="py-14 min-h-[40vh]">
        <div className="max-w-6xl mx-auto px-6">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i}><Skeleton className="aspect-[4/5]" /><Skeleton className="h-4 w-2/3 mt-4" /></div>
              ))}
            </div>
          ) : list.length ? (
            <>
              <p className="text-[12px] text-[#4a3d32] mb-8">{list.length} piece{list.length !== 1 ? 's' : ''}</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-12">
                {list.map((p) => <ProductCard key={p.id} product={p} onQuickView={setQuick} />)}
              </div>
            </>
          ) : (
            <div className="text-center py-20">
              <p className="font-display text-2xl mb-2">Nothing here yet</p>
              <p className="text-[#4a3d32] text-sm mb-6">This category has no published pieces. Explore the full collection instead.</p>
              <Link to="/products" className="text-[12px] uppercase tracking-[0.14em] text-[#b5622f] border-b border-[#b5622f] pb-0.5">View all products</Link>
            </div>
          )}
        </div>
      </section>

      {quick && <QuickViewModal product={quick} onClose={() => setQuick(null)} />}
    </>
  );
}
